/**
 * @fileoverview Constants for CXGN Table component
 * Extracted magic numbers and configuration values
 */

/**
 * Default configuration values for table rendering
 * @constant {Object}
 */
export const DEFAULTS = {
  // Pagination
  PAGE_LENGTH: 50,
  LENGTH_MENU: [[10, 25, 50, 100, -1], [10, 25, 50, 100, "All"]],

  // Virtual Scrolling
  VIRTUAL_SCROLL_THRESHOLD: 50,  // Number of rows before switching to virtual scrolling
  ROW_HEIGHT: 35,                 // Default row height in pixels
  BUFFER_SIZE: 15,                // Number of rows to buffer for virtual scrolling
  SCROLL_HEIGHT: '500px',         // Default scroll container height

  // Caching
  CACHE_ENABLED: true,
  CACHE_EXPIRY: 5 * 60 * 1000,   // 5 minutes in milliseconds

  // Network
  AJAX_TIMEOUT: 30000,            // 30 seconds
  MAX_RETRIES: 2,
  RETRY_BASE_DELAY: 1000,         // 1 second
  RETRY_MAX_DELAY: 5000,          // 5 seconds
  EXPORT_TIMEOUT: 300000,         // 5 minutes for large dataset exports

  // DataTables
  DOM_LAYOUT: 'lrtip',            // DataTables DOM positioning
  DEFAULT_ORDER: [[0, 'asc']],    // Default sort order
  AUTO_WIDTH: false,              // Disable auto width calculation

  // UI
  SEARCH_DEBOUNCE: 300,           // Debounce search input (ms)
  VIRTUAL_SCROLL_CONTAINER_HEIGHT: '400px',
  VIRTUAL_SCROLL_BORDER: '1px solid #ddd',

  // Export
  EXPORT_FILE_NAME: 'export',
  EXPORT_FORMATS: ['csv', 'excel', 'copy'],
};

/**
 * Table rendering modes
 * @enum {string}
 */
export const RENDER_MODE = {
  DATA_TABLES: 'datatables',
  VIRTUAL_SCROLL: 'virtualscroll',
  SERVER_SIDE: 'serverside',
  DOM_TABLE: 'domtable',
};

/**
 * Export formats
 * @enum {string}
 */
export const EXPORT_FORMAT = {
  CSV: 'csv',
  EXCEL: 'excel',
  COPY: 'copy',
  PRINT: 'print',
};

/**
 * Error codes for table operations
 * @enum {string}
 */
export const ERROR_CODE = {
  MISSING_CONTAINER: 'MISSING_CONTAINER',
  MISSING_COLUMNS: 'MISSING_COLUMNS',
  MISSING_DATA_SOURCE: 'MISSING_DATA_SOURCE',
  INVALID_RESPONSE: 'INVALID_RESPONSE',
  AJAX_ERROR: 'AJAX_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  NOT_FOUND: 'NOT_FOUND',
  SERVER_ERROR: 'SERVER_ERROR',
  EXPORT_ERROR: 'EXPORT_ERROR',
  INIT_ERROR: 'INIT_ERROR',
};

/**
 * HTTP status codes
 * @constant {Object}
 */
export const HTTP_STATUS = {
  OK: 200,
  NOT_FOUND: 404,
  SERVER_ERROR: 500,
};

/**
 * CSS class names
 * @constant {Object}
 */
export const CSS_CLASSES = {
  // Table classes
  TABLE_BASE: 'table table-bordered table-striped table-hover',
  TABLE_WRAPPER: 'cxgn-table-wrapper',
  TABLE_COMPACT: 'cxgn-table-compact',

  // State classes
  SELECTED_ROW: 'selected',
  PROCESSING: 'processing',

  // Loading
  LOADING: 'cxgn-table-loading',
  LOADING_LEGACY: 'text-center',

  // Error
  ERROR: 'cxgn-table-error',
  ALERT_DANGER: 'alert alert-danger',

  // Export
  EXPORT_FOOTER: 'cxgn-table-export-footer',
  EXPORT_BUTTONS: 'cxgn-table-export-buttons',
  EXPORT_LABEL: 'cxgn-table-export-label',
  EXPORT_BTN: 'btn cxgn-table-export-btn',
  EXPORT_LOADING: 'alert alert-info export-loading-msg',

  // Virtual scroll
  VIRTUAL_SCROLL_CONTAINER: 'virtual-scroll-container',
  SEARCH_CONTAINER: 'cxgn-table-search-container',

  // DataTables
  DATATABLES_WRAPPER: 'dataTables_wrapper',
  DATATABLES_INFO: 'dataTables_info',
  DATATABLES_PAGINATE: 'dataTables_paginate',
  UNIFIED_FOOTER: 'cxgn-table-unified-footer',
};

/**
 * Event types
 * @constant {Object}
 */
export const EVENTS = {
  CLICK: 'click',
  KEYUP: 'keyup',
  KEYDOWN: 'keydown',
  PROCESSING_DT: 'processing.dt',
  DRAW_DT: 'draw.dt',
};

/**
 * Glyphicon icon classes (Bootstrap 3)
 * @constant {Object}
 */
export const ICONS = {
  REFRESH: 'glyphicon glyphicon-refresh',
  REFRESH_SPIN: 'glyphicon glyphicon-refresh glyphicon-spin',
  EXCLAMATION: 'glyphicon glyphicon-exclamation-sign',
  SAVE_FILE: 'glyphicon glyphicon-save-file',
  FILE: 'glyphicon glyphicon-file',
  COPY: 'glyphicon glyphicon-copy',
  PRINT: 'glyphicon glyphicon-print',
};

/**
 * Validation rules
 * @constant {Object}
 */
export const VALIDATION = {
  MIN_COLUMNS: 0,
  MIN_PAGE_LENGTH: 1,
  MAX_TIMEOUT: 600000, // 10 minutes
};

/**
 * Performance metrics keys
 * @constant {Object}
 */
export const METRICS = {
  LOAD_TIME: 'loadTime',
  RENDER_TIME: 'renderTime',
  TOTAL_ROWS: 'totalRows',
  CACHE_HITS: 'cacheHits',
  CACHE_MISSES: 'cacheMisses',
};
